import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "maxsc_session_token";
// Per-tab demo token — owner "Open client view" feature stashes a Bearer
// token in sessionStorage so the new tab acts as the demo client without
// touching the coach's localStorage token in the original tab.
const DEMO_TOKEN_KEY = "maxsc_demo_session_token";

axios.defaults.withCredentials = true;

// On first paint of a new tab, capture the ?demo_token=... query param
// (set by DemoClientPanel) and stash it in sessionStorage so this tab is
// authenticated as the demo client for its lifetime. Strip the query param
// from the URL so the token doesn't show up in browser history.
function captureDemoTokenFromUrl() {
    if (typeof window === "undefined") return;
    try {
        const url = new URL(window.location.href);
        const t = url.searchParams.get("demo_token");
        if (!t) return;
        window.sessionStorage.setItem(DEMO_TOKEN_KEY, t);
        url.searchParams.delete("demo_token");
        window.history.replaceState({}, "", url.toString());
    } catch { /* ignore */ }
}
captureDemoTokenFromUrl();

// === Safari-ITP-proof session token fallback =========================
// Cookies set with SameSite=None;Secure get wiped by Safari ITP after 7 days
// of inactivity (and by some privacy extensions immediately). To keep users
// signed in across browser-cookie-flush events AND across server redeploys
// (in case the cookie was somehow lost), we also persist the session_token
// in localStorage and attach it as a `Authorization: Bearer <token>` header
// to every API request. Backend `get_current_user` prefers the Authorization
// header so per-tab demo tokens take precedence over the global cookie.
const tokenStore = {
    get: () => {
        try {
            if (typeof window === "undefined") return null;
            // Per-tab demo token wins — this is what makes "Open client view"
            // work without logging the coach out in the original tab.
            const demo = window.sessionStorage.getItem(DEMO_TOKEN_KEY);
            if (demo) return demo;
            return window.localStorage.getItem(TOKEN_KEY);
        } catch { return null; }
    },
    set: (t) => {
        try { if (typeof window !== "undefined") window.localStorage.setItem(TOKEN_KEY, t); }
        catch { /* private mode, ignore */ }
    },
    clear: () => {
        try {
            if (typeof window !== "undefined") {
                window.localStorage.removeItem(TOKEN_KEY);
                window.sessionStorage.removeItem(DEMO_TOKEN_KEY);
            }
        } catch { /* noop */ }
    },
};

// True if this tab is operating as a demo client (different visual treatment).
export function isDemoTab() {
    try {
        return typeof window !== "undefined" && !!window.sessionStorage.getItem(DEMO_TOKEN_KEY);
    } catch { return false; }
}

// Attach the bearer token to every outgoing API request. Idempotent — we
// install it exactly once at module load.
let interceptorInstalled = false;
function installAuthInterceptor() {
    if (interceptorInstalled) return;
    interceptorInstalled = true;
    axios.interceptors.request.use((config) => {
        const t = tokenStore.get();
        if (t && !config.headers?.Authorization) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${t}`;
        }
        return config;
    });
}
installAuthInterceptor();

const AuthContext = createContext(null);

// localStorage key for the currently-selected athlete on this account.
// Stored as `maxsc_active_athlete:<user_id>` so different parents on the
// same device don't clobber each other.
const ACTIVE_ATHLETE_PREFIX = "maxsc_active_athlete:";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeAthleteId, setActiveAthleteIdState] = useState(null);

    const checkAuth = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(r.data);
        } catch (err) {
            // If the token in localStorage is dead, clear it so the user gets
            // a clean sign-in path instead of an infinite 401 loop.
            if (err?.response?.status === 401) {
                tokenStore.clear();
            }
            // Don't downgrade an already-set user on a transient/401 response —
            // a parallel verify call may have already authenticated us.
            setUser((prev) => (prev ? prev : null));
        } finally {
            setLoading(false);
        }
    }, []);

    // Whenever the user object lands, resolve / persist the active athlete_id.
    // Rules:
    //   - Kid in Athlete Mode → that kid's athlete_id is always the active one
    //   - Parent w/ 1 kid → auto-select the only kid
    //   - Parent w/ N kids → restore last selection from localStorage; if
    //     none, leave null and let the UI prompt for a pick (athlete tabs)
    useEffect(() => {
        if (!user) { setActiveAthleteIdState(null); return; }
        if (user.athlete_mode_for) {
            setActiveAthleteIdState(user.athlete_mode_for);
            return;
        }
        const athletes = user.athletes || [];
        if (athletes.length === 0) { setActiveAthleteIdState(null); return; }
        if (athletes.length === 1) { setActiveAthleteIdState(athletes[0].athlete_id); return; }
        let stored = null;
        try { stored = window.localStorage.getItem(`${ACTIVE_ATHLETE_PREFIX}${user.user_id}`); } catch { /* noop */ }
        if (stored && athletes.some((a) => a.athlete_id === stored)) {
            setActiveAthleteIdState(stored);
        } else {
            // Default to first kid so workouts always have a target — the UI
            // still surfaces tabs so the parent can switch.
            setActiveAthleteIdState(athletes[0].athlete_id);
        }
    }, [user]);

    const setActiveAthleteId = useCallback((athleteId) => {
        setActiveAthleteIdState(athleteId);
        if (user?.user_id && athleteId) {
            try { window.localStorage.setItem(`${ACTIVE_ATHLETE_PREFIX}${user.user_id}`, athleteId); } catch { /* noop */ }
        }
    }, [user]);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback or magic-link click,
        // skip the /me check. AuthCallback / MagicLinkVerify will exchange
        // the session_id / token and establish the session first. Calling
        // /auth/me here races against verify and a 401 can overwrite the
        // freshly-set user — bouncing the user back to '/'.
        if (typeof window !== "undefined") {
            if (window.location.hash?.includes("session_id=")) {
                setLoading(false);
                return;
            }
            if (
                window.location.pathname.startsWith("/auth/") ||
                window.location.pathname.startsWith("/coach/accept")
            ) {
                setLoading(false);
                return;
            }
        }
        checkAuth();
    }, [checkAuth]);

    const requestMagicLink = async (email) => {
        const redirect_url = `${window.location.origin}/auth/magic`;
        const r = await axios.post(`${API}/auth/magic-link/request`, { email, redirect_url });
        return r.data;
    };

    const verifyMagicLink = async (token) => {
        const r = await axios.post(
            `${API}/auth/magic-link/verify`,
            { token },
            { withCredentials: true },
        );
        if (r.data?.session_token) tokenStore.set(r.data.session_token);
        setUser(r.data.user);
        return r.data.user;
    };

    const exchangeGoogleSession = async (sessionId) => {
        const r = await axios.post(
            `${API}/auth/google/session`,
            { session_id: sessionId },
            { withCredentials: true },
        );
        if (r.data?.session_token) tokenStore.set(r.data.session_token);
        setUser(r.data.user);
        return r.data.user;
    };

    const acceptCoachInvite = async (token) => {
        const r = await axios.post(
            `${API}/coach/invite/accept`,
            { token },
            { withCredentials: true },
        );
        await checkAuth();
        return r.data.user;
    };

    const startGoogleLogin = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch { /* noop */ }
        tokenStore.clear();
        setUser(null);
    };

    const clearStuckSession = async () => {
        // Server-side: delete the cookie + any matching session row.
        try { await axios.post(`${API}/auth/clear-session`, {}, { withCredentials: true }); }
        catch { /* noop */ }
        // Client-side: also nuke the localStorage fallback so a corrupt token
        // can't keep resurrecting the broken session on every request.
        tokenStore.clear();
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                checkAuth,
                refreshUser: checkAuth,
                requestMagicLink,
                verifyMagicLink,
                exchangeGoogleSession,
                acceptCoachInvite,
                startGoogleLogin,
                logout,
                clearStuckSession,
                activeAthleteId,
                setActiveAthleteId,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
